<script setup lang="ts">
import { computed, toRefs } from "vue"

const props = defineProps({
  total: { type: Number, required: true },
  perPage: { type: Number, required: true },
  page: { type: Number, required: true },
  url: { type: String, required: true },
})

const { total, perPage, page, url } = toRefs(props)

// total number of pages
// 검색 결과가 0건이면 ceil(0/perPage) = 0 이 되어 Next 의 disabled 조건(page === totalPages)이
// 성립하지 않았다. 그래서 결과 없는 화면에서 Next 가 눌리고, makePageLink 의 상한 clamp 가
// page 를 0 으로 낮춰 서버가 400 "page is invalid" 로 거절했다(PARTNER-FRONTEND-XT).
// 결과가 없어도 화면상 페이지는 1페이지다.
const totalPages = computed(() => Math.max(1, Math.ceil(total.value / perPage.value)))

// build page list with ellipses
const pages = computed(() => {
  const result = []
  const last = totalPages.value
  const current = page.value
  const delta = 2
  const start = Math.max(2, current - delta)
  const end = Math.min(last - 1, current + delta)

  result.push(1)

  if (start > 2) {
    result.push("...")
  }

  for (let i = start; i <= end; i++) {
    result.push(i)
  }

  if (end < last - 1) {
    result.push("...")
  }

  if (last > 1) {
    result.push(last)
  }

  return result
})

// generate link with updated `page` query param
function makePageLink(p: number) {
  let pageNum = p
  if (pageNum < 1) pageNum = 1
  if (pageNum > totalPages.value) pageNum = totalPages.value

  try {
    // merge into existing URL
    const u = new URL(url.value, window.location.origin)
    u.searchParams.set("page", String(pageNum))
    const qs = u.searchParams.toString()
    return u.pathname + (qs ? `?${qs}` : "")
  } catch {
    // fallback: naive concatenation
    const sep = url.value.includes("?") ? "&" : "?"
    return `${url.value}${sep}page=${pageNum}`
  }
}
</script>

<template>
  <nav aria-label="Page navigation">
    <ul class="pagination">
      <!-- Previous -->
      <li class="page-item" :class="{ disabled: page === 1 }">
        <router-link
          class="page-link"
          :to="makePageLink(page - 1)"
          aria-label="Previous"
          :tabindex="page === 1 ? -1 : 0"
        >
          <span aria-hidden="true">&laquo;</span>
        </router-link>
      </li>
      <!-- Page numbers and ellipses -->
      <li
        v-for="(p, idx) in pages"
        :key="idx"
        class="page-item"
        :class="{ active: p === page, disabled: typeof p !== 'number' }"
      >
        <span v-if="typeof p !== 'number'" class="page-link"> {{ p }} </span>
        <router-link v-else class="page-link" :to="makePageLink(p)">{{ p }}</router-link>
      </li>
      <!-- Next -->
      <li class="page-item" :class="{ disabled: page === totalPages }">
        <router-link
          class="page-link"
          :to="makePageLink(page + 1)"
          aria-label="Next"
          :tabindex="page === totalPages ? -1 : 0"
        >
          <span aria-hidden="true">&raquo;</span>
        </router-link>
      </li>
    </ul>
  </nav>
</template>

<style scoped>
nav {
  margin-top: 0.75rem;
}

.page-item.disabled .page-link {
  pointer-events: none;
  opacity: 0.5;
}

@media (max-width: 991.98px) {
  .page-link {
    font-size: 14px !important;
    padding: 0.3rem 0.6rem !important;
  }
}
</style>
